import { Stack } from '@mui/material'
import dayjs from 'dayjs'
import React, { useCallback, useEffect, useMemo, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { useAppDispatch, useAppSelector } from '../../store/redux/hooks'
import { pageHeaderActions } from '../../store/redux/reducers/pageHeaderSlice'
import { PageIndex, setPageIndex, setPageName } from '../../store/redux/reducers/pageNameSlice'
import { StyledScheduleGrid } from '../../styles/MasonryStyles'
import { StyledPageContent } from '../../styles/PageStyles'
import { getSelectedDate, monthPickerActions } from '../../store/redux/reducers/monthPickerSlice'
import { getCurrentUser, getIsLeaderOrManagement } from '../../store/redux/reducers/currentUserSlice'
import {
  PlannedHours,
  TransformedPlannedHours,
  useGetScheduleQuery,
} from '../../store/redux/reducers/scheduleDateSlice'
import MasonryTileHours from './MasonryTileHours'
import { useGetUsersWorklogQuery } from '../../store/redux/reducers/worklogSlice'
import { User } from '../../store/types'
import { useNavigate, useParams } from 'react-router-dom'
import { breadcrumbsItemsActions } from '../../store/redux/reducers/breadcrumbsSlice'
import navigation from '../../config/navigation'
import InfoSidebar from '../../components/Sidebar/info/InfoSidebar'
import UserSelector from '../../components/UserSelector/UserSelector'
import DateFormat from '../../store/types/enums/DateFormat'
import Loading from '../../components/Loading/Loading'
import { useGetLightUsersQuery } from '../../store/redux/reducers/usersSlice'
import checkAccessDenied from '../../store/utils/checkAccessDenied'
import DiscardChangesDialog from '../../components/Dialog/DiscardChangesDialog'
import { useSelector } from 'react-redux'

export const statusOptions = ['PLANNED', 'TRACKED']

export const CheckWeekDay = (date: string): boolean => {
  const dayjsDate = dayjs(date)
  return !(dayjsDate.get('day') === 0 || dayjsDate.get('day') === 6)
}

const ScheduleView = () => {
  const { t } = useTranslation()
  const dispatch = useAppDispatch()
  const navigate = useNavigate()
  const selectedDate = useAppSelector(state => getSelectedDate(state))
  const currentUser = useSelector(getCurrentUser)
  const isLeaderOrManagement = useSelector(getIsLeaderOrManagement)

  const mobileScheduleMinWidthPx = '640px'

  const yearMonth = useMemo(() => {
    const date = selectedDate || dayjs()
    return dayjs(date).format(DateFormat.YearMonth)
  }, [selectedDate])

  const { id: rawId } = useParams()
  const id = useMemo(() => rawId || currentUser?.id, [rawId, currentUser?.id])

  const { data: employeesResult, isLoading } = useGetScheduleQuery({ employee: id, date: yearMonth })

  useEffect(() => {
    dispatch(setPageName(t('page.name.schedule')))
    dispatch(setPageIndex(PageIndex.SCHEDULE))

    dispatch(
      pageHeaderActions.setButtons({
        monthPicker: true,
      })
    )

    dispatch(monthPickerActions.setDate(dayjs().toString()))

    return () => {
      dispatch(pageHeaderActions.setInitialState())
    }
  }, [dispatch, t])

  const [plannedHours, setPlannedHours] = useState<PlannedHours>({})
  const [trackedHours, setTrackedHours] = useState<PlannedHours>({})
  const [oldPlannedHours, setOldPlannedHours] = useState<PlannedHours>({})
  const [oldTrackedHours, setOldTrackedHours] = useState<PlannedHours>({})

  const convertToPlannedHours = useCallback(
    (transformed: TransformedPlannedHours[]) => {
      const result: PlannedHours = {}
      for (let i = 1; i <= dayjs(selectedDate).daysInMonth(); i++) {
        const day = dayjs(selectedDate).set('date', i)
        result[day.toISOString().split('T')[0]] = [{ id: i.toString(), from: '-', to: '-' }]
      }
      for (const entry of transformed) {
        const { date, timeSlots } = entry
        result[date] = timeSlots.map(timeSlot => {
          const { id, from, to } = timeSlot
          return { id, from: from ? from.slice(0, -3) : '', to: to ? to.slice(0, -3) : '' }
        })
      }
      for (const day in result) {
        result[day].sort((a, b) => {
          if (a.from === '-') {
            return 1
          }
          if (b.from === '-') {
            return -1
          }
          return a.from.localeCompare(b.from)
        })
      }
      return result
    },
    [selectedDate]
  )

  useEffect(() => {
    if (employeesResult) {
      const planHours = convertToPlannedHours(employeesResult.plannedHours)
      const trackHours = convertToPlannedHours(employeesResult.trackedHours)
      setPlannedHours(planHours)
      setTrackedHours(trackHours)
      setOldPlannedHours(planHours)
      setOldTrackedHours(trackHours)
    }
  }, [employeesResult, selectedDate, convertToPlannedHours])

  const {
    data: worklog,
    error,
    isLoading: isWorklogLoading,
  } = useGetUsersWorklogQuery({
    employeeId: id || currentUser?.id,
    date: yearMonth,
    all: false,
  })

  const { data: selectedUserData } = useGetLightUsersQuery(
    { page: 0, size: 1, id: id || currentUser?.id },
    { skip: !isLeaderOrManagement }
  )

  const [selectedUser, setSelectedUser] = useState<User | null>(selectedUserData?.content[0] || currentUser)

  useEffect(() => {
    if (selectedUserData) {
      setSelectedUser(selectedUserData.content[0])
    }
  }, [selectedUserData])

  useEffect(() => {
    if (selectedUser) {
      dispatch(breadcrumbsItemsActions.setInitialState())
      dispatch(
        breadcrumbsItemsActions.setSelectedItems({
          schedule: `${t('page.name.schedule')} - ${selectedUser.firstName} ${selectedUser.lastName}`,
        })
      )
    }
  }, [dispatch, selectedUser, t])

  useEffect(() => {
    if (error) {
      if (checkAccessDenied(error)) {
        navigate(navigation.unauthorized)
      }
    }
  }, [error, navigate])

  const [isCancelDialogOpen, setIsCancelDialogOpen] = useState(false)
  const [isChangePresent, setIsChangePresent] = useState(false)
  const checkUnsavedChanges = () => {
    return isChangePresent
  }

  const copyIntoTracked = (newHours: (prevState: PlannedHours) => PlannedHours) => {
    setTrackedHours(prevState => newHours(prevState))
    setOldTrackedHours(prevState => newHours(prevState))
  }

  const copyIntoPlanned = (newHours: (prevState: PlannedHours) => PlannedHours) => {
    setPlannedHours(prevState => newHours(prevState))
    setOldPlannedHours(prevState => newHours(prevState))
  }

  return isLoading || isWorklogLoading ? (
    <Loading />
  ) : (
    <StyledPageContent>
      <Stack direction="row" justifyContent="space-between" py={2} gap={1}>
        <Stack sx={{ width: { xs: '100%', md: '50%', lg: '25%' } }}>
          {isLeaderOrManagement && (
            <UserSelector
              selectedUser={selectedUser}
              setSelectedUser={setSelectedUser}
              currentUser={currentUser}
              currentView={navigation.schedule}
            />
          )}
        </Stack>
        <InfoSidebar variant="schedule" size="medium" />
      </Stack>

      {currentUser?.id === id ? (
        <StyledScheduleGrid
          sx={{
            minWidth: { xs: mobileScheduleMinWidthPx, sm: 'auto' },
            paddingBottom: 3,
          }}
        >
          <MasonryTileHours
            hours={plannedHours}
            reverseHours={trackedHours}
            setHours={setPlannedHours}
            copyInto={copyIntoTracked}
            isPlannedHours
            variant="planned"
            selectedUser={selectedUser}
            setIsChangePresent={setIsChangePresent}
            oldHours={oldPlannedHours}
          />
          <MasonryTileHours
            hours={trackedHours}
            reverseHours={plannedHours}
            setHours={setTrackedHours}
            copyInto={copyIntoPlanned}
            isPlannedHours={false}
            worklog={worklog}
            variant="tracked"
            selectedUser={selectedUser}
            setIsChangePresent={setIsChangePresent}
            oldHours={oldTrackedHours}
          />
        </StyledScheduleGrid>
      ) : (
        <StyledScheduleGrid
          sx={{
            minWidth: { xs: mobileScheduleMinWidthPx, sm: 'auto' },
          }}
        >
          <MasonryTileHours
            hours={plannedHours}
            reverseHours={trackedHours}
            setHours={setPlannedHours}
            isPlannedHours
            variant="planned"
            isReadOnly
            selectedUser={selectedUser}
            oldHours={oldPlannedHours}
          />
          <MasonryTileHours
            hours={trackedHours}
            reverseHours={plannedHours}
            setHours={setTrackedHours}
            isPlannedHours={false}
            worklog={worklog}
            variant="tracked"
            isReadOnly
            selectedUser={selectedUser}
            oldHours={oldTrackedHours}
          />
        </StyledScheduleGrid>
      )}
      <DiscardChangesDialog
        checkUnsavedChanges={checkUnsavedChanges}
        open={isCancelDialogOpen}
        setIsCancelDialogOpen={setIsCancelDialogOpen}
      />
    </StyledPageContent>
  )
}

export default ScheduleView
